import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Button, Card, CardContent, Container, Paper, Typography } from "@mui/material";
import Seo from "../components/Seo";
import { SITE } from "../data/site";
import { buildBreadcrumbSchema, buildPersonSchema, buildWebPageSchema } from "../utils/schema";
import "./About.css";

const photoUrl = `${process.env.PUBLIC_URL}/assets/NatePhoto.png`;

export default function About() {
  const title = "About — Brainwave Solutions";
  const description =
    "Learn more about Nate Parish, founder of Brainwave Solutions, with 20+ years of experience in Excel, data analytics, and practical business problem-solving.";
  const canonical = "https://brainwavesolutions.net/about";
  const structuredData = [
    buildWebPageSchema({ title, description, canonical, type: "AboutPage" }),
    buildBreadcrumbSchema([
      { name: "Home", url: SITE.url },
      { name: "About", url: canonical },
    ]),
    buildPersonSchema(),
  ];

  return (
    <>
      <Seo
        title={title}
        description={description}
        canonical={canonical}
        image={`${SITE.url}/assets/NatePhoto.png`}
        structuredData={structuredData}
      />

      <Box className="bws-aboutPage">
        <Container maxWidth="lg">
          <div className="bws-aboutHero">
            <div>
              <Typography variant="h1" className="bws-aboutTitle">
                About
              </Typography>

              <p className="bws-aboutLead">
                I’m Nate Parish, founder of Brainwave Solutions. I built this business around a simple idea: a lot of
                people do not need more complexity — they need someone who can understand the problem, think through
                the options, and build the best solution for the way they actually work.
              </p>

              <p className="bws-aboutLead bws-aboutLeadTight">
                I have more than 20 years of experience working with Excel, data analysis, reporting, websites, E-commerce, and systems that
                help people make sense of information. A lot of my work naturally sits at the intersection of data, workflows, problem-solving, and the tools businesses
                rely on every day.
              </p>

              <div className="bws-aboutBadges">
                <span className="bws-aboutBadge">20+ years of experience</span>
                <span className="bws-aboutBadge">Based in the USA</span>
                <span className="bws-aboutBadge">Native English speaker</span>
                <span className="bws-aboutBadge">Direct communication</span>
              </div>

              <div className="bws-aboutCtas">
                <Button component={RouterLink} to="/contact" variant="contained" className="bws-btn">
                  Contact
                </Button>
                <Button component={RouterLink} to="/services" variant="outlined" className="bws-btnGhost">
                  View services
                </Button>
              </div>
            </div>

            <Paper className="bws-aboutPhotoCard" elevation={0}>
              <img
                className="bws-aboutPhoto"
                src={photoUrl}
                alt="Portrait of Nate Parish, founder of Brainwave Solutions"
              />
              <div className="bws-aboutPhotoMeta">
                <Typography className="bws-aboutName">Nate Parish</Typography>
                <Typography className="bws-aboutRole">Founder, Brainwave Solutions</Typography>
              </div>
            </Paper>
          </div>

          <div className="bws-aboutSection">
            <div className="bws-aboutGrid">
              <Card className="bws-aboutCard" elevation={0}>
                <CardContent className="bws-aboutCardContent">
                  <Typography className="bws-aboutH2">How I got here</Typography>
                  <p className="bws-aboutP">
                    My work naturally moved toward solving real operational problems: fixing spreadsheet systems,
                    cleaning up reporting, organizing messy data, improving workflows, and building practical tools when
                    a simple file was no longer enough.
                  </p>
                  <p className="bws-aboutP">
                    Over the years, I have spent a lot of time helping people turn confusing, fragile, or manual
                    processes into something clearer and more dependable. That might mean a one-time spreadsheet fix, a
                    better dashboard, a more polished website, or a workflow that saves hours every month.
                  </p>
                </CardContent>
              </Card>

              <Card className="bws-aboutCard" elevation={0}>
                <CardContent className="bws-aboutCardContent">
                  <Typography className="bws-aboutH2">How I work with clients</Typography>
                  <p className="bws-aboutP">
                    I try to keep projects straightforward. You tell me what is not working, what result you want, and
                    any constraints that matter. From there, I focus on building the best solution for the situation —
                    not just something quick, and not something overly complicated for the sake of it.
                  </p>
                  <p className="bws-aboutP">
                    You also work directly with me. There is no handoff chain, no bloated agency process, and no
                    unnecessary layers between the problem and the solution. That means faster communication,
                    clearer expectations, and work that stays grounded in the real need.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="bws-aboutSection">
            <Paper className="bws-aboutWideCard" elevation={0}>
              <Typography className="bws-aboutH2">What Brainwave Solutions is built around</Typography>
              <div className="bws-aboutReasons">
                <div className="bws-aboutReason">
                  <Typography className="bws-aboutReasonTitle">Clarity</Typography>
                  <Typography className="bws-aboutReasonText">
                    A lot of business friction comes from things being unclear — confusing spreadsheets, scattered
                    processes, messy data, or websites that do not communicate well.
                  </Typography>
                </div>

                <div className="bws-aboutReason">
                  <Typography className="bws-aboutReasonTitle">Reliability</Typography>
                  <Typography className="bws-aboutReasonText">
                    It is not enough for something to sort of work. I aim for solutions that are easier to trust, easier
                    to maintain, and less fragile in day-to-day use.
                  </Typography>
                </div>

                <div className="bws-aboutReason">
                  <Typography className="bws-aboutReasonTitle">Practical value</Typography>
                  <Typography className="bws-aboutReasonText">
                    I am not interested in adding complexity for its own sake. The goal is to create something that
                    genuinely saves time, reduces friction, or makes better decisions easier.
                  </Typography>
                </div>
              </div>
            </Paper>
          </div>

          <div className="bws-aboutSection">
            <div className="bws-aboutGrid">
              <Card className="bws-aboutCard" elevation={0}>
                <CardContent className="bws-aboutCardContent">
                  <Typography className="bws-aboutH2">A good fit if you want…</Typography>
                  <ul className="bws-aboutList">
                    <li>Direct communication with the person doing the work</li>
                    <li>Thoughtful, practical problem-solving</li>
                    <li>Solutions that are useful in real day-to-day work</li>
                    <li>Someone who can work across spreadsheets, reporting, data, websites, and tools</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bws-aboutCard" elevation={0}>
                <CardContent className="bws-aboutCardContent">
                  <Typography className="bws-aboutH2">Typical clients</Typography>
                  <ul className="bws-aboutList">
                    <li>Small and mid-sized businesses</li>
                    <li>Business owners and operations teams</li>
                    <li>People who need one-time help with a specific problem</li>
                    <li>Anyone who wants a cleaner, more reliable workflow</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          <Paper className="bws-aboutBottomCta" elevation={0}>
            <div>
              <Typography className="bws-ctaTitle">Need help with a project?</Typography>
              <p className="bws-muted">
                Send a quick description of what is not working and what you want the end result to be.
              </p>
            </div>
            <Button component={RouterLink} to="/contact" variant="contained" className="bws-btn">
              Contact
            </Button>
          </Paper>
        </Container>
      </Box>
    </>
  );
}
